export abstract class ColorHelper {

    /**
     * Calculate color-difference according to W3C
     * Link: https://www.w3.org/TR/AERT/#color-contrast
     *
     * @static
     * @param {string} color1
     * @param {string} color2
     * @returns {number}
     * @memberof ColorHelper
     */
    static colorDifference(color1: string, color2: string): number {
        const colorObject1 = new ColorObject(color1);
        const colorObject2 = new ColorObject(color2);

        return (Math.max(colorObject1.red, colorObject2.red) - Math.min(colorObject1.red, colorObject2.red))
            + (Math.max(colorObject1.green, colorObject2.green) - Math.min(colorObject1.green, colorObject2.green))
            + (Math.max(colorObject1.blue, colorObject2.blue) - Math.min(colorObject1.blue, colorObject2.blue));
    }

    /**
     * Calculate relative luminance according to W3C
     * Link: https://www.w3.org/TR/2008/REC-WCAG20-20081211/#relativeluminancedef
     *
     * @static
     * @param {number} RsRGB
     * @param {number} GsRGB
     * @param {number} BsRGB
     * @returns {number}
     * @memberof ColorHelper
     */
    static relativeLuminance(color: string): number {
        const object = new ColorObject(color);
        const red = object.red;
        const green = object.green;
        const blue = object.blue;

        const R = red <= 0.03928 ? red / 12.92 : Math.pow((red + 0.055) / 1.055, 2.4);
        const G = green <= 0.03928 ? green / 12.92 : Math.pow((green + 0.055) / 1.055, 2.4);
        const B = blue <= 0.03928 ? blue / 12.92 : Math.pow((blue + 0.055) / 1.055, 2.4);

        return 0.2126 * R + 0.7152 * G + 0.0722 * B;
    }

    /**
     * Calculate contrast ratio according to W3C
     * (range from 1 to 21, commonly written 1:1 to 21:1)
     * Link: https://www.w3.org/TR/2008/REC-WCAG20-20081211/#contrast-ratiodef
     *
     * @static
     * @param {string} hex representation of lighter color
     * @param {string} hex representation of darker color
     * @returns {number}
     * @memberof ColorHelper
     */
    static contrastRatio(lighter: string, darker: string): number {
        return (ColorHelper.relativeLuminance(lighter) + 0.05) / (ColorHelper.relativeLuminance(darker) + 0.05);
    }

}

class ColorObject {

    hexColor: string;

    constructor(hex: string) {
        this.hexColor = hex.replace('#', '');
    }

    public get red() {
        return parseInt(this.hexColor.substr(0, 2), 16);
    }

    public get green() {
        return parseInt(this.hexColor.substr(2, 2), 16);
    }

    public get blue() {
        return parseInt(this.hexColor.substr(4, 2), 16);
    }

    public setRgb(r: number, g: number, b: number) {
        this.hexColor = r.toString(16) + g.toString(16) + b.toString(16);
    }

}
